import {
  ComponentRef,
  DestroyRef,
  Injector,
  Type,
  ViewContainerRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
  Subject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import { FormProperty } from 'src/app/core/models/form.models';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { ObjectPermission } from 'src/app/core/models/core.models';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { generateUUID } from 'src/app/shared/utils/uuid';
import { toObservable } from '@angular/core/rxjs-interop';

export interface TabConfig {
  id: string;
  label: string;
  component: Type<any>;
  icon?: IconProp;
  inputs?: Record<string, any>;
  // * Object-level permissions required to show this tab (e.g., ['can_manage'])
  // * Tab is hidden if the object doesn't have ALL required permissions
  objectPermissions?: ObjectPermission[];
  count?: number;
  userAccessLevel?: UserAccessLevel;
  // * Exclude this tab's form values from combineValues() (for tabs that manage their own data structure)
  excludeFromValues?: boolean;
}

// * optional interface that TAB Components can implement
export interface TabComponent {
  // * Observable stream of FormGroup changes (for dynamic forms)
  formGroupChange?: Subject<FormGroup>;
  // * Direct FormGroup reference (for static forms)
  formGroup?: FormGroup;
  // * Optional method to trigger form creation
  createFormGroup?: () => void;
  // * Optional stream for custom component data
  componentDataChange?: Subject<any>;

  activeFormProperties?: FormProperty[];
}

// ═════════════════════════════════════════════════════════════════════════════
// * TAB MANAGER FACTORY
// ═════════════════════════════════════════════════════════════════════════════

/**
 * @example usage
 * const tabManager = injectTabManager();
 * tabManager.initialize(viewContainerRef, tabConfigs);
 * tabManager.selectTab('component-id');
 */
export function injectTabManager() {
  const injector = inject(Injector);
  const destroyRef = inject(DestroyRef);
  const logger = inject(LoggerService);

  // ───────────────────────────────────────────────────────────────────────────
  // * INTERNAL STATE
  // ───────────────────────────────────────────────────────────────────────────

  // ! Slot where tab components are dynamically rendered
  let vcRef: ViewContainerRef | undefined;
  let tabConfigs: TabConfig[] = [];

  // * Component instances cached by tab ID (for detach/reattach -> no destroy)
  const componentRefs = new Map<string, ComponentRef<any>>();
  // * Component-level subscriptions (formGroupChange, etc.)
  const subscriptions = new Map<string, Subscription[]>();
  // * FormGroup statusChanges subscriptions for reactive validation
  const statusSubscriptions = new Map<string, Subscription>();

  // ───────────────────────────────────────────────────────────────────────────
  // * REACTIVE STATE (SIGNALS)
  // ───────────────────────────────────────────────────────────────────────────

  // * Signal containing all tracked FormGroups by tab ID
  const formGroups = signal(new Map<string, FormGroup>());

  const activeTabId = signal<string | null>(null);

  const isDirty = computed(() => {
    return Array.from(formGroups().values()).some((fg) => fg.dirty);
  });

  // * Computed: true if all tracked FormGroups are valid or disabled
  // ! Note: DISABLED forms return valid=false, but are acceptable for submission
  const isValid = computed(() => {
    const groups = Array.from(formGroups().values());
    return (
      groups.length > 0 &&
      groups.every((fg) => fg.status === 'VALID' || fg.status === 'DISABLED')
    );
  });

  // * Observable stream of form value changes for the currently active tab
  // * Used only in Widgets Settings TWO PANE view
  // ! Emits whenever the active tab changes OR its FormGroup values change OR FormGroups get registered
  // ! FIX: Watch BOTH activeTabId AND formGroups signal to handle async FormGroup registration
  const activeFormValueChanges$ = combineLatest([
    toObservable(activeTabId, { injector }),
    toObservable(formGroups, { injector }),
  ]).pipe(
    switchMap(([tabId, formGroupsMap]) => {
      if (!tabId) {
        return of(null);
      }

      // * Get the FormGroup for the active tab from the signal map
      const fg = formGroupsMap.get(tabId);

      if (!fg) {
        return of(null);
      }

      // * Emit current value immediately, then subsequent changes
      return fg.valueChanges.pipe(startWith(fg.value));
    }),
  );

  // ───────────────────────────────────────────────────────────────────────────
  // * COMPONENT LIFECYCLE MANAGEMENT
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Creates and attaches a tab component to the DOM.
   * Component is hidden by default (display: none).
   */
  function createTabComponent(tab: TabConfig): void {
    if (!vcRef) {
      console.warn('[TabManager] ViewContainerRef not initialized');
      return;
    }

    // * Create component instance with injector context
    const compRef = vcRef.createComponent(tab.component, { injector });

    // * Apply configured inputs to component instance
    if (tab.inputs) {
      Object.assign(compRef.instance, tab.inputs);
    }

    // * Hide component by default (will be shown when selected)
    const element = compRef.location.nativeElement as HTMLElement;
    element.style.display = 'none';

    // * Cache component reference for future reuse
    componentRefs.set(tab.id, compRef);

    // * Set up subscriptions for form tracking
    trackComponentForms(tab.id, compRef);
  }

  // * Sets up subscriptions to track FormGroup changes from a tab component
  function trackComponentForms(
    tabId: string,
    compRef: ComponentRef<TabComponent>,
  ): void {
    const subs: Subscription[] = [];
    const instance = compRef.instance;

    // * Pattern 1: Observable FormGroup (for dynamic/async forms)
    if (instance.formGroupChange) {
      const sub = instance.formGroupChange
        .pipe(
          startWith(instance.formGroup), // * emit initial value if exists
          filter((fg): fg is FormGroup => fg != null),
          distinctUntilChanged(), // * prevent duplicate registration of same instance
          tap((fg) => registerFormGroup(tabId, fg)),
        )
        .subscribe();

      subs.push(sub);
    }
    // * Pattern 2: Direct FormGroup (for static forms)
    else if (instance.formGroup) {
      registerFormGroup(tabId, instance.formGroup);
    }

    // * store subscriptions for cleanup
    subscriptions.set(tabId, subs);

    // ! Register cleanup when component is destroyed
    compRef.onDestroy(() => {
      subs.forEach((s) => s.unsubscribe());
      subscriptions.delete(tabId);
      unregisterFormGroup(tabId);
      componentRefs.delete(tabId);
    });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // * FORMGROUP REGISTRATION & TRACKING
  // ───────────────────────────────────────────────────────────────────────────

  function registerFormGroup(tabId: string, fg: FormGroup): void {
    // * Add FormGroup to signal Map
    const currentMap = formGroups();
    currentMap.set(tabId, fg);
    // ! Create new Map reference to trigger signal reactivity
    formGroups.set(new Map(currentMap));

    // * Clean up previous statusChanges subscription if exists
    statusSubscriptions.get(tabId)?.unsubscribe();

    // * Subscribe to FormGroup statusChanges (VALID/INVALID/PENDING/DISABLED)
    // ! this handles async validators and dynamic control additions
    const statusSub = fg.statusChanges.subscribe(() => {
      // ! Trigger signal update by creating new Map reference
      formGroups.set(new Map(formGroups()));
    });

    statusSubscriptions.set(tabId, statusSub);
  }

  function unregisterFormGroup(tabId: string): void {
    statusSubscriptions.get(tabId)?.unsubscribe();
    statusSubscriptions.delete(tabId);

    const currentMap = formGroups();
    currentMap.delete(tabId);
    formGroups.set(new Map(currentMap));
  }

  // ───────────────────────────────────────────────────────────────────────────
  // * VIEW MANAGEMENT (CSS SHOW/HIDE)
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Hides the currently active tab using CSS display:none
   */
  function hideCurrentTab(): void {
    const currentActiveId = activeTabId();
    if (!currentActiveId) return;

    const currentRef = componentRefs.get(currentActiveId);
    if (currentRef) {
      const element = currentRef.location.nativeElement as HTMLElement;
      element.style.display = 'none';
    }
  }

  /**
   * Shows the specified tab by dropping the inline display:none.
   * The host keeps whatever display its own stylesheet declares (a flex
   * column that owns an inner scroll region, for instance); an inline
   * display:block here would override it. Custom elements without a host
   * display rule render inline, so those fall back to block.
   */
  function showTab(tabId: string): void {
    const compRef = componentRefs.get(tabId);
    if (compRef) {
      const element = compRef.location.nativeElement as HTMLElement;
      element.style.removeProperty('display');
      if (getComputedStyle(element).display === 'inline') {
        element.style.display = 'block';
      }
      activeTabId.set(tabId);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // * PUBLIC API
  // ───────────────────────────────────────────────────────────────────────────

  /**
   * Initializes tab manager and creates ALL tab components.
   * All tabs are inserted into DOM but hidden (display:none).
   */
  function initialize(
    viewContainerRef: ViewContainerRef,
    tabs: TabConfig[],
  ): void {
    vcRef = viewContainerRef;
    tabConfigs = tabs;

    // * Create and insert ALL tabs into DOM (hidden by default)
    tabs.forEach((tab) => createTabComponent(tab));
  }

  /**
   * Reconciles the tab set with a list that changed after initialize() -
   * for dialogs whose tabs arrive or rebuild asynchronously. Components are
   * created for new ids and destroyed for removed ones; surviving ids keep
   * their component instance (inputs are not re-applied). When the active
   * tab is removed, selection falls back to the first tab.
   */
  function syncTabs(tabs: TabConfig[]): void {
    if (!vcRef) {
      // ! Pre-initialize() emission: initialize() will pick up the caller's
      // ! current tab array itself.
      return;
    }

    const nextIds = new Set(tabs.map((tab) => tab.id));

    // * destroy() runs each component's onDestroy cleanup, which also removes
    // * it from componentRefs and unregisters its FormGroup
    componentRefs.forEach((ref, id) => {
      if (!nextIds.has(id)) {
        ref.destroy();
      }
    });

    tabConfigs = tabs;

    tabs.forEach((tab) => {
      if (!componentRefs.has(tab.id)) {
        createTabComponent(tab);
      }
    });

    const currentActiveId = activeTabId();
    if (currentActiveId && !nextIds.has(currentActiveId) && tabs.length > 0) {
      selectTab(tabs[0].id);
    }
  }

  // * generic TAB filtering utility
  function filterTabs(
    tabs: TabConfig[],
    predicate: (tab: TabConfig) => boolean,
  ): TabConfig[] {
    return tabs.filter(predicate);
  }

  /**
   * Switches to the specified tab by hiding current and showing target.
   */
  function selectTab(tabId: string): void {
    const tab = tabConfigs.find((t) => t.id === tabId);
    if (!tab) {
      return;
    }

    hideCurrentTab();
    showTab(tabId);
  }

  function getInvalidTabIds(): string[] {
    const invalidIds: string[] = [];
    formGroups().forEach((fg, tabId) => {
      if (fg.invalid) {
        invalidIds.push(tabId);
        logger.debug(`Tab ${tabId} is invalid.`);
        logger.debug(fg);
      }
    });
    return invalidIds;
  }

  /**
   * Counts invalid FormControls recursively in a FormGroup or FormArray
   */
  function countInvalidControls(control: FormGroup | FormArray): number {
    let count = 0;

    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach((key) => {
        const childControl = control.get(key);
        if (!childControl) return;

        if (
          childControl instanceof FormGroup ||
          childControl instanceof FormArray
        ) {
          count += countInvalidControls(childControl);
        } else if (childControl.invalid) {
          count++;
        }
      });
    } else if (control instanceof FormArray) {
      control.controls.forEach((childControl) => {
        if (
          childControl instanceof FormGroup ||
          childControl instanceof FormArray
        ) {
          count += countInvalidControls(childControl);
        } else if (childControl.invalid) {
          count++;
        }
      });
    }

    return count;
  }

  /**
   * Returns a map of tab IDs to their invalid field counts
   */
  function getInvalidFieldCounts(): Record<string, number> {
    const counts: Record<string, number> = {};

    formGroups().forEach((fg, tabId) => {
      const invalidCount = countInvalidControls(fg);
      if (invalidCount > 0) {
        counts[tabId] = invalidCount;
      }
    });

    return counts;
  }
  /**
   * If tab component do not have active form properties after filtering
   * hide the tab from the list
   * @param tabs
   * @returns
   */
  function filterTabsByAvailableActiveFormProperties(
    tabs: TabConfig[],
  ): TabConfig[] {
    return tabs.filter((tab) => {
      const component = getTabComponent<TabComponent>(tab.id);

      return (
        !component?.activeFormProperties ||
        component?.activeFormProperties.length > 0
      );
    });
  }

  function markAllAsTouched(): void {
    formGroups().forEach((fg) => fg.markAllAsTouched());
  }

  function markActiveFormGroupAsTouched(fg: FormGroup) {
    Object.values(fg.controls).forEach((control) => {
      control.markAsTouched();
      control.markAsDirty();

      // If the control is a FormGroup or FormArray, recursively mark its children as touched
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markActiveFormGroupAsTouched(control as any);
      }
    });
  }

  /**
   * Triggers validation on all registered FormGroups.
   * Uses emitEvent:false to avoid side effects.
   */
  function updateAllValidity(): void {
    formGroups().forEach((fg) => {
      fg.updateValueAndValidity({ emitEvent: false });
    });
  }

  function addFormGroup(fg: FormGroup): void {
    const key = generateUUID();

    formGroups.update((map) => {
      const newMap = new Map(map);
      newMap.set(key, fg);

      return newMap;
    });
  }

  function combineValues(): Record<string, any> {
    const combined: Record<string, any> = {};
    formGroups().forEach((fg, tabId) => {
      // * Skip tabs marked with excludeFromValues
      const tabConfig = tabConfigs.find((t) => t.id === tabId);
      if (tabConfig?.excludeFromValues) {
        return;
      }
      Object.assign(combined, fg.value);
    });
    return combined;
  }

  function getTabComponent<T = any>(tabId: string): T | undefined {
    return componentRefs.get(tabId)?.instance;
  }

  function getTabFormGroup(tabId: string): FormGroup | undefined {
    return formGroups().get(tabId);
  }

  /**
   * checks if a Tab component has formProperties (extends GeneralDynamicFormComponent)
   * useful for determining if a Tab supports Search filtering
   */
  function hasFormProperties(tabId: string): boolean {
    const component = getTabComponent(tabId);
    return !!component?.formProperties;
  }

  /**
   * Destroys and recreates a specific tab component.
   * Useful for resetting tab state without closing the dialog.
   */
  function reloadTab(tabId: string): void {
    const tab = tabConfigs.find((t) => t.id === tabId);
    if (!tab) return;

    const wasActive = activeTabId() === tabId;

    const existing = componentRefs.get(tabId);
    if (existing) {
      existing.destroy();
      componentRefs.delete(tabId);
      createTabComponent(tab);
    }
    if (wasActive) {
      showTab(tabId);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // * CLEANUP
  // ───────────────────────────────────────────────────────────────────────────

  destroyRef.onDestroy(() => {
    componentRefs.forEach((ref) => ref.destroy());
    subscriptions.forEach((subs) => subs.forEach((s) => s.unsubscribe()));
    statusSubscriptions.forEach((sub) => sub.unsubscribe());
    componentRefs.clear();
    formGroups.set(new Map());
    subscriptions.clear();
    statusSubscriptions.clear();
  });

  // ───────────────────────────────────────────────────────────────────────────
  // * RETURN PUBLIC API
  // ───────────────────────────────────────────────────────────────────────────

  return {
    // * Initialization
    initialize,
    syncTabs,

    // * Navigation
    selectTab,
    activeTabId: activeTabId.asReadonly(),

    // * Validation state (reactive signals)
    isDirty,
    isValid,
    getInvalidTabIds,
    getInvalidFieldCounts,

    // * Form operations
    markAllAsTouched,
    markActiveFormGroupAsTouched,
    updateAllValidity,
    combineValues,
    addFormGroup,
    filterTabsByAvailableActiveFormProperties,

    // * Form value changes stream
    activeFormValueChanges$,

    // * Component access
    getTabComponent,
    getTabFormGroup,

    // * Utilities
    filterTabs,
    hasFormProperties,

    // * Advanced operations
    reloadTab,
  };
}
